import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { FiUser, FiBriefcase, FiFileText, FiCheckCircle } from "react-icons/fi";
import editProfileUser from "assets/images/others/host-side-images/editProfileUSer.png";
import businsessProfileBg from "assets/images/others/host-side-images/businessBg.png";
import { ReactComponent as EditProfile } from "assets/images/svgs/edit-profiles.svg";
import { ReactComponent as InforCircle } from "assets/images/svgs/info-circle.svg";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import { useSelector } from "react-redux";
import PhoneNumberInput from "components/phone-number-input";
import { HostProfileDTO } from "utils/helpers/models/host-dtos/host-profile.dto";
import useHook from "./useHooks";
import { warningToaster, errorToaster } from "utils/helpers/common/alert-service";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import TermsAndCondition from "components/drawers/host-side/TermsAndCondtion";
import PlacesAutocomplete from "components/forms/places-autocomplete";
import FormErrorMessage from "components/forms/form-error-message";
import useAuth from "containers/auth/useHooks";
import { GENDERS, INDUSTRY_TYPES } from "utils/helpers/enums/shared.enum";
import HostPlacesAutocomplete from "components/forms/places-autocomplete/HostPlacesAutoComplete";
import { useJsApiLoader } from "@react-google-maps/api";
import useStore from "hooks/useStore";
import { useNavigate } from "react-router-dom";

// Define libraries array outside component to prevent reloading
const libraries: "places"[] = ["places"];

const HostProfile: React.FC = () => {
  const [formData, setFormData] = useState<HostProfileDTO>(
    new HostProfileDTO()
  );
  const [oldFormData, setOldFormData] = useState<HostProfileDTO>(
    new HostProfileDTO()
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    control,
    watch,
  } = useForm<HostProfileDTO>({ defaultValues: formData });
  const [currentStep, setCurrentStep] = useState(1);
  const [isMissionTooltipVisible, setIsMissionTooltipVisible] = useState(false);
  const [isAboutTooltipVisible, setIsAboutTooltipVisible] = useState(false);
  const [termsDrawerOpen, setTermsDrawerOpen] = useState<boolean>(false);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const isFetchingZipRef = useRef<boolean>(false);
  const initialStateRef = useRef<string | null>(null);
  const navigate = useNavigate();

  const { uploadFile, updateProfile, updateBusinessProfile } = useHook();

  const { getStates } = useAuth();
  const { isProfileCompleted } = useStore();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  // Load Google Maps API for geocoding
  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API || "",
    libraries,
  });

  useEffect(() => {
    if (isGoogleMapsLoaded && !geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
  }, [isGoogleMapsLoaded]);

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  // State abbreviation to full name mapping
  const stateAbbreviationToFullName: { [key: string]: string } = {
    AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
    CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
    HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
    KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
    MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
    MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
    NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
    OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
    SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
    VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
    DC: "District of Columbia"
  };

  // Helper function to convert state abbreviation to full name
  const convertStateToFullName = (state: string): string => {
    if (!state) return "";
    const upperState = state.toUpperCase().trim();
    // If it's already a full name (more than 2 characters), return as is
    if (upperState.length > 2) {
      // Check if it's already a full name by checking if it exists in the mapping values
      const isFullName = Object.values(stateAbbreviationToFullName).some(
        (fullName) => fullName.toLowerCase() === state.toLowerCase()
      );
      if (isFullName) return state;
    }
    // Convert abbreviation to full name
    return stateAbbreviationToFullName[upperState] || state;
  };

  // Helper function to parse address components
  const parseAddressComponents = (address: string) => {
    if (!address) return { city: "", state: "", zipCode: "" };

    // Split by comma to get main parts
    const parts = address.split(", ");

    if (parts.length >= 3) {
      // Format: "Street Address, City, State ZIP"
      const city = parts[1] || "";
      const stateZipPart = parts[2] || "";

      // Split state and ZIP (assuming format like "CA 90043" or "IN 46122")
      const stateZipParts = stateZipPart.trim().split(" ");
      let state = stateZipParts[0] || "";
      const zipCode = stateZipParts[1] || "";

      // Convert state abbreviation to full name
      state = convertStateToFullName(state);

      return { city, state, zipCode };
    } else if (parts.length === 2) {
      // Format: "Street Address, City State ZIP" or "Street Address, City"
      const secondPart = parts[1] || "";
      const secondPartSplit = secondPart.trim().split(" ");

      if (secondPartSplit.length >= 2) {
        // Try to extract state and ZIP from the last two parts
        const city = secondPartSplit.slice(0, -2).join(" ");
        let state = secondPartSplit[secondPartSplit.length - 2] || "";
        const zipCode = secondPartSplit[secondPartSplit.length - 1] || "";

        // Convert state abbreviation to full name
        state = convertStateToFullName(state);

        return { city, state, zipCode };
      } else {
        // Just city
        return { city: secondPart, state: "", zipCode: "" };
      }
    }

    return { city: "", state: "", zipCode: "" };
  };

  useEffect(() => {
    // Populate form data first to set initial state
    populateFormData();

    // Load states and populate cities if state is already set
    getStates((loadedStates) => {
      setStates(loadedStates);
      // After states are loaded, check for state value from multiple sources
      // Priority: form value (from react-hook-form) > ref > registeredData > formData state
      const stateFromForm = getValues("state");
      const stateFromRef = initialStateRef.current;
      const stateFromRegisteredData =
        registeredData?.profiles?.host?.state || registeredData?.user?.state;
      const stateFromFormData = formData.state;
      const currentState =
        stateFromForm || stateFromRef || stateFromRegisteredData || stateFromFormData;

      if (currentState && loadedStates.length > 0) {
        // Immediately populate cities with the loaded states
        populateCitiesForState(currentState, loadedStates);
      }
    });
    register("placeId", { required: false });
    register("businessAddress", { required: false });
    register("city", { required: false });
    register("state", { required: false });
    register("zipCode", { required: false });
  }, []);

  // Helper function to populate cities based on selected state
  const populateCitiesForState = (stateName: string, statesList?: any[]) => {
    const statesToUse = statesList || states;
    if (stateName && statesToUse.length > 0) {
      const state = statesToUse.find((s) => s.name === stateName);
      if (state && state.cities && Array.isArray(state.cities)) {
        setCities([...state.cities]);
        return true;
      } else {
        setCities([]);
        return false;
      }
    }
    return false;
  };

  // Watch state value from react-hook-form to react to changes
  const watchedState = watch("state");

  useEffect(() => {
    // When states are loaded, check for state value from form or formData
    const currentState = watchedState || formData.state || getValues("state");
    
    if (currentState && states.length > 0) {
      populateCitiesForState(currentState, states);
    } else if (!currentState && states.length > 0) {
      setCities([]);
    }
  }, [states, watchedState, formData.state]);

  // Auto-populate zip code when state and city are selected
  useEffect(() => {
    const fetchZipCode = () => {
      // Only fetch if both state and city are selected, zip code is empty, and geocoder is available
      if (
        formData.state &&
        formData.city &&
        !formData.zipCode &&
        geocoderRef.current &&
        !isFetchingZipRef.current
      ) {
        isFetchingZipRef.current = true;
        const address = `${formData.city}, ${formData.state}, USA`;

        geocoderRef.current.geocode({ address }, (results, status) => {
          isFetchingZipRef.current = false;
          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results[0]
          ) {
            const components = results[0].address_components || [];
            const zipCodeComponent = components.find((c) =>
              c.types.includes("postal_code")
            );

            if (zipCodeComponent) {
              const zipCode = zipCodeComponent.long_name;
              setFormData((prev) => ({ ...prev, zipCode }));
              setValue("zipCode", zipCode, { shouldValidate: true });
            }
          }
        });
      }
    };

    // Only fetch if Google Maps is loaded
    if (isGoogleMapsLoaded) {
      fetchZipCode();
    }
  }, [formData.state, formData.city, isGoogleMapsLoaded, setValue]);

  // Watch phone number fields and sync to formData (PhoneNumberInput updates form state via Controller)
  const phoneValue = watch("phone");
  const businessPhoneValue = watch("businessPhoneNumber");

  useEffect(() => {
    if (phoneValue !== undefined) {
      const trimmedValue = phoneValue?.trim() || phoneValue;
      setFormData((prev) => {
        if (prev.phone !== trimmedValue) {
          return { ...prev, phone: trimmedValue };
        }
        return prev;
      });
    }
  }, [phoneValue]);

  useEffect(() => {
    if (businessPhoneValue !== undefined) {
      const trimmedValue = businessPhoneValue?.trim() || businessPhoneValue;
      setFormData((prev) => {
        if (prev.businessPhoneNumber !== trimmedValue) {
          return { ...prev, businessPhoneNumber: trimmedValue };
        }
        return prev;
      });
    }
  }, [businessPhoneValue]);

  const populateFormData = async () => {
    const combinedData = {
      ...registeredData?.user,
      ...registeredData?.profiles?.host,
    };
    const imageFields = [
      "profilePicUrl",
      "businessPicUrl",
      "driverLicenseFrontPicUrl",
      "driverLicenseBackPicUrl",
    ];
    Object.keys(combinedData).forEach((key) => {
      formData[key] = combinedData[key] ?? "";
      if (imageFields.includes(key)) {
        formData[key] = combinedData[key]?.fileDownloadUri ?? "";
        // console.log(formData[key], key);
      }
      setValue(key as keyof HostProfileDTO, formData[key]);
    });

    // Store the state value for later use when states load
    if (combinedData.state) {
      initialStateRef.current = combinedData.state;
    }

    // Ensure businessAddress is properly set for the autocomplete component
    if (combinedData.businessAddress) {
      formData.businessAddress = combinedData.businessAddress;
      formData.address = combinedData.businessAddress; // Keep both for compatibility
      setValue("businessAddress", combinedData.businessAddress);

      // Extract city, state, zip from businessAddress if individual fields are empty
      if (!combinedData.city || !combinedData.state || !combinedData.zipCode) {
        const { city, state, zipCode } = parseAddressComponents(
          combinedData.businessAddress
        );

        // Only set if the individual fields are empty
        if (!combinedData.city && city) {
          formData.city = city;
          setValue("city", city);
        }
        if (!combinedData.state && state) {
          formData.state = state;
          setValue("state", state);
        }
        if (!combinedData.zipCode && zipCode) {
          formData.zipCode = zipCode;
          setValue("zipCode", zipCode);
        }
      }
    }

    setFormData({ ...formData });
    setOldFormData({ ...formData });
  };

  const steps = [
    { id: 1, title: "Host", icon: <FiUser size={20} /> },
    { id: 2, title: "Business Info", icon: <FiBriefcase size={20} /> },
    { id: 3, title: "Declaration", icon: <FiFileText size={20} /> },
  ];

  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },

    { title: "Host Profile", path: siteRoutes.hostProfile },
  ];

  const handleChange = (event: any) => {
    const {
      target: { value, name },
    } = event;
    setValue(name, value, { shouldValidate: true });

    // If state or city changes, clear zip code to allow auto-population
    if (name === "state" || name === "city") {
      const updatedFormData = { ...formData, [name]: value };
      if (name === "state") {
        // Clear city and zip when state changes
        updatedFormData.city = "";
        updatedFormData.zipCode = "";
        setValue("city", "", { shouldValidate: true });
        setValue("zipCode", "", { shouldValidate: true });

        // Immediately populate cities for the selected state
        populateCitiesForState(value);
      } else if (name === "city") {
        // Clear zip when city changes and clear validation error
        updatedFormData.zipCode = "";
        setValue("zipCode", "", { shouldValidate: true });
        // Clear city validation error by triggering validation
        setValue("city", value, { shouldValidate: true });
      }
      setFormData(updatedFormData);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const hasChanged = (fields: string[]) => {
    let changed = false;
    for (let key in formData) {
      if (fields.includes(key)) {
        if (Array.isArray(formData[key])) {
          if (
            !(
              formData[key].length === oldFormData[key].length &&
              formData[key].every((i) => oldFormData[key].includes(i))
            )
          ) {
            changed = true;
            break;
          }
        } else {
          if (formData[key] !== oldFormData[key]) {
            changed = true;
            break;
          }
        }
      }
    }

    return changed;
  };

  const toggleTermsDrawer = () => {
    setTermsDrawerOpen(!termsDrawerOpen);
  };

  const onSubmit = async () => {
    if (currentStep === 1) {
      const profilePic = getValues("profilePicUrl");
      if (!profilePic) {
        warningToaster("Please upload your profile picture");
        return;
      }
      const fields: string[] = [
        "profilePicUrl",
        "firstName",
        "lastName",
        "email",
        "phone",
        "dob",
        "gender",
      ];

      const changed = hasChanged(fields);
      if (changed) {
        const profilePic = getValues("profilePicUrl");
        if (typeof profilePic !== "string") {
          const form_data = new FormData();
          form_data.append("file", profilePic);
          const imageData: any = await uploadFile(form_data);
          // console.log(imageData, "imageData");
          
          // Check if upload was successful
          if (!imageData || !imageData.id) {
            const errorMessage = imageData?.error || "Failed to upload profile picture. Please try again with a supported image format.";
            errorToaster(errorMessage);
            return;
          }
          
          formData.profilePicId = imageData.id;
        } else {
          if (
            !formData.profilePicId &&
            registeredData?.user?.profilePicUrl?.id
          ) {
            formData.profilePicId = registeredData?.user?.profilePicUrl?.id;
          }
        }

        const dobValue = getValues("dob");
        if (dobValue) {
          formData.dob = new Date(dobValue).toISOString();
        }

        // Get phone number from form and sync to formData (PhoneNumberInput updates form state, not formData)
        const phoneValue = getValues("phone");
        if (phoneValue) {
          formData.phone = phoneValue.trim();
        }

        updateProfile(formData, goNext, formData, setOldFormData);
      } else {
        goNext();
      }
    } else if (currentStep === 2) {
      const businessPic = getValues("businessPicUrl");
      if (!businessPic) {
        warningToaster("Please upload your business picture");
        return;
      }
      goNext();
    } else {
      const businessPic = getValues("businessPicUrl");
      if (typeof businessPic !== "string") {
        const form_data = new FormData();
        form_data.append("file", businessPic);
        const imageData: any = await uploadFile(form_data);
        
        // Check if upload was successful
        if (!imageData || !imageData.id) {
          const errorMessage = imageData?.error || "Failed to upload business picture. Please try again with a supported image format.";
          errorToaster(errorMessage);
          return;
        }
        
        formData.businessPicId = imageData.id;
      } else {
        if (
          !formData.businessPicId &&
          registeredData?.profiles?.host?.businessPicUrl?.id
        ) {
          formData.businessPicId =
            registeredData?.profiles?.host?.businessPicUrl?.id;
        }
      }
      const licenseFrontPic = getValues("driverLicenseFrontPicUrl");
      if (!licenseFrontPic) {
        warningToaster("Please upload your ID front image");
        return;
      }
      if (typeof licenseFrontPic !== "string") {
        const form_data = new FormData();
        form_data.append("file", licenseFrontPic);
        const imageData: any = await uploadFile(form_data);
        
        // Check if upload was successful
        if (!imageData || !imageData.id) {
          const errorMessage = imageData?.error || "Failed to upload ID front image. Please try again with a supported image format.";
          errorToaster(errorMessage);
          return;
        }
        
        formData.driverLicenseFrontPicId = imageData.id;
      } else {
        if (
          !formData.driverLicenseFrontPicId &&
          registeredData?.profiles?.host?.driverLicenseFrontPicUrl?.id
        ) {
          formData.driverLicenseFrontPicId =
            registeredData?.profiles?.host?.driverLicenseFrontPicUrl?.id;
        }
      }

      const licenseBackPic = getValues("driverLicenseBackPicUrl");
      if (!licenseBackPic) {
        warningToaster("Please upload your ID Back image");
        return;
      }
      if (typeof licenseBackPic !== "string") {
        const form_data = new FormData();
        form_data.append("file", licenseBackPic);
        const imageData: any = await uploadFile(form_data);
        
        // Check if upload was successful
        if (!imageData || !imageData.id) {
          const errorMessage = imageData?.error || "Failed to upload ID back image. Please try again with a supported image format.";
          errorToaster(errorMessage);
          return;
        }
        
        formData.driverLicenseBackPicId = imageData.id;
      } else {
        if (
          !formData.driverLicenseBackPicId &&
          registeredData?.profiles?.host?.driverLicenseBackPicUrl?.id
        ) {
          formData.driverLicenseBackPicId =
            registeredData?.profiles?.host?.driverLicenseBackPicUrl?.id;
        }
      }
      const fields = [
        "businessPicId",
        "businessName",
        "businessEmail",
        "businessPhoneNumber",
        "businessWebsite",
        "missionStatement",
        "about",
        "industry",
        "businessAddress",
        "state",
        "city",
        "zipCode",
        "driverLicenseFrontPicId",
        "driverLicenseBackPicId",
        "ambassadorReferralCode",
        "placeId",
      ];

      // Get business phone number from form and sync to formData (PhoneNumberInput updates form state, not formData)
      const businessPhoneValue = getValues("businessPhoneNumber");
      if (businessPhoneValue) {
        formData.businessPhoneNumber = businessPhoneValue.trim();
      }

      const body: any = {};
      for (let key in formData) {
        if (fields.includes(key)) body[key] = formData[key];
      }

      updateBusinessProfile(body);
    }
  };

  const goNext = () => {
    if (currentStep < 3) {
      register("businessAddress", { required: currentStep === 2 });
      register("city", { required: currentStep === 2 });
      register("state", { required: currentStep === 2 });
      register("zipCode", { required: currentStep === 2 });
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const goPrevious = () => {
    if (currentStep > 1) {
      register("businessAddress", { required: currentStep === 3 });
      register("city", { required: currentStep === 3 });
      register("state", { required: currentStep === 3 });
      register("zipCode", { required: currentStep === 3 });
      setCurrentStep((prevStep) => {
        return prevStep - 1;
      });
    }
  };

  const uploadProfilePic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("profilePicUrl", file);
    setFormData({ ...formData, profilePicUrl: URL.createObjectURL(file) });
  };

  const uploadBusinessPic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("businessPicUrl", file);
    setFormData({ ...formData, businessPicUrl: URL.createObjectURL(file) });
  };

  const uploadLicenseFrontPic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("driverLicenseFrontPicUrl", file);
    setFormData({
      ...formData,
      driverLicenseFrontPicUrl: URL.createObjectURL(file),
    });
  };

  const uploadLicenseBackPic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("driverLicenseBackPicUrl", file);
    setFormData({
      ...formData,
      driverLicenseBackPicUrl: URL.createObjectURL(file),
    });
  };

  return (
    <div className="container  min-h-screen">
      <Breadcrumb links={breadcrumbLinks} />

      <form onSubmit={handleSubmit(onSubmit)} className="">
        {/* Stepper */}
        <div className="flex items-center mb-8 relative w-full md:w-1/2">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col justify-center w-full">
              <div className="flex items-center w-full">
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${
                    currentStep > step.id
                      ? "border-yellowText bg-yellowText "
                      : currentStep === step.id
                      ? "border-yellowText bg-white text-yellowText"
                      : "border-grayDarkText bg-white text-grayDarkText"
                  }`}
                >
                  {currentStep > step.id ? (
                    <FiCheckCircle className="text-white" />
                  ) : (
                    step.icon
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 ${
                      currentStep > step.id
                        ? "bg-yellowText"
                        : currentStep === step.id
                        ? "bg-grayDarkText"
                        : "bg-grayDarkText"
                    }`}
                  ></div>
                )}
              </div>
              <p
                className={`mt-2  text-sm text-start font-medium ${
                  currentStep === step.id
                    ? "text-yellowText"
                    : currentStep > step.id
                    ? "text-yellowText"
                    : "text-gray-500"
                }`}
              >
                {step.title}
              </p>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="">
          {currentStep === 1 && (
            <div key={currentStep} className="">
              <div className="flex flex-col items-center w-[270px]">
                <div className="relative">
                  <img
                    src={
                      formData.profilePicUrl
                        ? concatPathWithBackendUrl(formData.profilePicUrl)
                        : editProfileUser
                    }
                    alt="Profile"
                    className="w-36 h-36 rounded-full object-cover"
                  />
                  {formData.profilePicUrl ? (
                    <button
                      type="button"
                      onClick={() =>
                        document.getElementById("profilePicUrl")?.click()
                      }
                      className="absolute bottom-2 right-0 bg-white  text-gray-700 p-2 rounded-full shadow-md cursor-pointer"
                    >
                      <EditProfile />
                    </button>
                  ) : (
                    <label
                      htmlFor="profilePicUrl"
                      className="absolute bottom-2 right-0 bg-white  text-gray-700 p-2 rounded-full shadow-md cursor-pointer"
                    >
                      <EditProfile />
                    </label>
                  )}
                  <input
                    id="profilePicUrl"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    {...register("profilePicUrl", { required: false })}
                    onChange={uploadProfilePic}
                  />
                </div>
                {errors.profilePicUrl && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.profilePicUrl.message}
                  </p>
                )}
                <div className="flex flex-col gap-2 items-center mt-3">
                  <p className="text-base text-label px-5 font-semibold">
                    Upload Profile Picture
                  </p>
                  <p className="text-labelTextColor text-center text-sm">
                    For security purposes, only use updated/recent picture of
                    host by which attendees can identify you
                  </p>
                </div>
              </div>

              <div>
                <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      First name
                    </label>
                    <input
                      type="text"
                       className="custom-input cursor-not-allowed"
                      {...register("firstName", {
                        required: "First name is required",
                      })}
                      value={formData.firstName}
                      onChange={handleChange}
                      disabled
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Last name
                    </label>
                    <input
                      type="text"
                      className="custom-input cursor-not-allowed"
                      {...register("lastName", {
                        required: "Last name is required",
                      })}
                      value={formData.lastName}
                      onChange={handleChange}
                      disabled
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Email
                    </label>
                    <input
                      type="email"
                      className="custom-input"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: "Enter a valid email",
                        },
                      })}
                      readOnly
                      disabled
                      value={formData.email}
                      // onChange={handleChange}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      placeholder="Select Date"
                      className="custom-input"
                      max={
                        new Date(
                          new Date().getFullYear() - 13,
                          new Date().getMonth(),
                          new Date().getDate()
                        )
                          .toISOString()
                          .split("T")[0]
                      } // calendar restrict
                      {...register("dob", {
                        required: "Date of Birth is required",
                        validate: (value) => {
                          const dob = new Date(value);
                          const today = new Date();
                          const minAgeDate = new Date(
                            today.getFullYear() - 13,
                            today.getMonth(),
                            today.getDate()
                          );
                          return (
                            dob <= minAgeDate ||
                            "You must be at least 13 years old"
                          );
                        },
                      })}
                    />
                    {errors.dob && (
                      <p className="text-red-500 text-sm">
                        {errors.dob.message}
                      </p>
                    )}
                  </div>

                  {/* Gender Field */}
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Gender
                    </label>
                    <select
                      className="custom-input"
                      {...register("gender", {
                        required: "Gender is required",
                      })}
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">Select Gender</option>
                      {Object.values(GENDERS).map((gender, index) => {
                        return (
                          <option value={gender} key={index}>
                            {gender}
                          </option>
                        );
                      })}
                    </select>
                    {errors.gender && (
                      <p className="text-red-500 text-sm">
                        {errors.gender.message}
                      </p>
                    )}
                  </div>

                  {/* Phone Number Field */}
                  <PhoneNumberInput
                    control={control}
                    name="phone"
                    label="Phone Number"
                    error={errors.phone}
                    placeholder="(123) 456-7890"
                    required={false}
                    className="custom-input"
                    labelClassName="text-label text-base font-semibold"
                    gap="gap-1"
                  />
                </div>
              </div>
            </div>
          )}
          {currentStep === 2 && (
            <div key={currentStep}>
              <div className="flex flex-col ">
                <div className="relative  w-full">
                  <img
                    src={
                      formData.businessPicUrl
                        ? concatPathWithBackendUrl(formData.businessPicUrl)
                        : businsessProfileBg
                    }
                    alt="Profile"
                    className="w-full h-[300px] rounded-xl object-center"
                  />
                  {formData.businessPicUrl ? (
                    <button
                      type="button"
                      onClick={() =>
                        document.getElementById("businessPicUrl")?.click()
                      }
                      className="absolute bottom-[-20px] right-16 bg-white shadow p-2 rounded-full cursor-pointer"
                    >
                      <EditProfile />
                    </button>
                  ) : (
                    <label
                      htmlFor="businessPicUrl"
                      className="absolute bottom-[-20px] right-16 bg-white shadow p-2 rounded-full cursor-pointer"
                    >
                      <EditProfile />
                    </label>
                  )}
                  <input
                    id="businessPicUrl"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    {...register("businessPicUrl", { required: false })}
                    onChange={uploadBusinessPic}
                  />
                </div>
                {errors.businessPicUrl && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.businessPicUrl.message}
                  </p>
                )}
                <div className="flex flex-col gap-2 items-start w-full sm:w-1/2 mt-3">
                  <p className="text-base text-label font-semibold">
                    Upload Business Picture
                  </p>
                  <p className="text-labelTextColor text-start text-sm">
                    For security purposes, only use an updated/recent picture of
                    the business by which the attendee can easily identify the
                    business by when arriving to the location. External
                    storefront is recommended. A business logo is not
                    acceptable.
                  </p>
                </div>
              </div>

              <div>
                <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Business name
                    </label>
                    <input
                      type="text"
                      className="custom-input"
                      {...register("businessName", {
                        required: "Business Name is required",
                      })}
                      value={formData.businessName}
                      onChange={handleChange}
                      disabled={oldFormData.businessName ? true : false}
                    />
                    {errors.businessName && (
                      <p className="text-red-500 text-sm">
                        {errors.businessName.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Business Email
                    </label>
                    <input
                      type="email"
                      className="custom-input"
                      {...register("businessEmail", {
                        required: "Email is required",
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: "Enter a valid email",
                        },
                      })}
                      value={formData.businessEmail}
                      onChange={handleChange}
                    />
                    {errors.businessEmail && (
                      <p className="text-red-500 text-sm">
                        {errors.businessEmail.message}
                      </p>
                    )}
                  </div>
                  {/* Phone Number Field */}
                  <PhoneNumberInput
                    control={control}
                    name="businessPhoneNumber"
                    label="Business Phone Number"
                    error={errors.businessPhoneNumber}
                    placeholder="(123) 456-7890"
                    required={false}
                    className="custom-input"
                    labelClassName="text-label text-base font-semibold"
                    gap="gap-1"
                  />
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Business Website
                    </label>
                    <input
                      type="text"
                      className="custom-input"
                      {...register("businessWebsite")}
                      value={formData.businessWebsite}
                      onChange={handleChange}
                    />
                    {errors.businessWebsite && (
                      <p className="text-red-500 text-sm">
                        {errors.businessWebsite.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1 relative">
                    <label className="custom-label">
                      Mission
                    </label>
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        className="custom-input"
                        {...register("missionStatement", {
                          required: "Business Mission is required",
                        })}
                        value={formData.missionStatement}
                        onChange={handleChange}
                      />
                      {/* Info Icon */}
                      <div
                        className="absolute right-2 cursor-pointer"
                        onMouseEnter={() => setIsMissionTooltipVisible(true)}
                        onMouseLeave={() => setIsMissionTooltipVisible(false)}
                      >
                        <InforCircle />
                      </div>

                      {/* Tooltip */}
                      {isMissionTooltipVisible && (
                        <div className="absolute w-[230px] h-fit top-[-100px] right-3 p-4 rounded-t-lg rounded-bl-lg bg-primaryBlueBg text-white text-xs opacity-100 transition-opacity duration-300 z-10">
                          Tell us how your business and/or educational
                          experiences will enhance the community. This is your
                          opportunity to connect with the community through
                          shared values.
                        </div>
                      )}
                    </div>

                    {errors.missionStatement && (
                      <p className="text-red-500 text-sm">
                        {errors.missionStatement.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 relative">
                    <label className="custom-label">
                      About
                    </label>
                    <div className="relative flex items-center">
                      <input
                        className="custom-input"
                        {...register("about", {
                          required: "About Business is required",
                        })}
                        value={formData.about}
                        onChange={handleChange}
                      />
                      {/* Info Icon */}
                      <div
                        className="absolute right-2 cursor-pointer"
                        onMouseEnter={() => setIsAboutTooltipVisible(true)}
                        onMouseLeave={() => setIsAboutTooltipVisible(false)}
                      >
                        <InforCircle />
                      </div>

                      {/* Tooltip */}
                      {isAboutTooltipVisible && (
                        <div className="absolute w-[230px] h-fit top-[-100px] right-3 p-4 rounded-t-lg rounded-bl-lg bg-primaryBlueBg text-white text-xs opacity-100 transition-opacity duration-300 z-10">
                          Tell your audience some fun or unique details about
                          your business. Include anything parents/kids would
                          enjoy learning about which may contribute to a
                          stronger connection.
                        </div>
                      )}
                    </div>

                    {errors.about && (
                      <p className="text-red-500 text-sm">
                        {errors.about.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 my-4">
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Industry
                    </label>
                    <select
                      {...register("industry", {
                        required: "Industry is required",
                      })}
                      value={formData.industry}
                      onChange={handleChange}
                      className="custom-input"
                    >
                      <option className="h-10" value="">
                        Select
                      </option>
                      {Object.values(INDUSTRY_TYPES).map((industry) => (
                        <option
                          key={industry}
                          value={industry}
                          className="h-[40px]"
                        >
                          {industry.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                    {errors.industry && (
                      <p className="text-red-500 text-sm">
                        {errors.industry.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Address
                    </label>
                    {/* // Replace the HostPlacesAutocomplete onChange handler (around line 665): */}

                    <HostPlacesAutocomplete
                      value={formData.businessAddress || formData.address}
                      onChange={(data) => {
                        setFormData({
                          ...formData,
                          businessAddress: data.address,
                          address: data.address,
                          city: data.city,
                          state: data.state,
                          zipCode: data.zipCode,
                        });

                        // Update form fields and CLEAR validation errors
                        setValue("businessAddress", data.address, {
                          shouldValidate: true,
                        });
                        setValue("city", data.city || "", {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue("state", data.state || "", {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue("zipCode", data.zipCode || "", {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        if (data.city) {
                          setTimeout(() => {
                            setValue("city", data.city, {
                              shouldValidate: true,
                            });
                          }, 0);
                        }

                        // Populate cities for the selected state
                        if (data.state) {
                          populateCitiesForState(data.state);
                        }
                      }}
                    />

                    <FormErrorMessage error={errors.businessAddress} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
                  {/* State Input */}
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      State
                    </label>
                    <select
                      className="custom-input"
                      {...register("state", {
                        required: "State is required",
                      })}
                      value={getValues("state") || formData.state}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {states.map((state, index) => (
                        <option key={index} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    {errors.state && (
                      <p className="text-red-500 text-sm">
                        {errors.state.message}
                      </p>
                    )}
                  </div>

                  {/* City Input */}
                  {/* City Input */}
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      City
                    </label>
                    <select
                      className="custom-input"
                      {...register("city", {
                        required: "City is required",
                      })}
                      value={formData.city || ""}
                      onChange={(e) => {
                        const newCity = e.target.value;
                        handleChange(e);
                        // Immediately update and validate
                        setFormData((prev) => ({
                          ...prev,
                          city: newCity,
                          zipCode: "",
                        }));
                        setValue("city", newCity, {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                        setValue("zipCode", "", { shouldValidate: true });
                      }}
                    >
                      <option value="">Select</option>
                      {cities.map((city, index) => (
                        <option key={index} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                    {errors.city && (
                      <p className="text-red-500 text-sm">
                        {errors.city.message}
                      </p>
                    )}
                  </div>

                  {/* Zip Code Input */}
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      className="custom-input"
                      {...register("zipCode", {
                        required: "Zip code is required",
                        pattern: {
                          value: /^[0-9]{5}(?:-[0-9]{4})?$/,
                          message: "Please enter a valid ZIP code",
                        },
                      })}
                      value={getValues("zipCode") || ""}
                      onChange={handleChange}
                    />
                    {errors.zipCode && (
                      <p className="text-red-500 text-sm">
                        {errors.zipCode.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentStep === 3 && (
            <div key={currentStep}>
              <p className="text-base font-semibold text-primaryBlueText">
                Declaration
              </p>
              <div>
                <div className="flex w-full md:w-2/3 my-3 items-start">
                  <input
                    type="checkbox"
                    id="declarationAccepted"
                    className="custom-checkbox mt-[2px]"
                    {...register("declarationAccepted", {
                      required: "You must accept the declaration to proceed",
                    })}
                  />
                  <div className="ms-2">
                    <p className="text-labelTextColor text-base">
                      I hereby declare that I have thoroughly reviewed and
                      understood the{" "}
                      <span
                        onClick={toggleTermsDrawer}
                        className="text-yellowText cursor-pointer"
                      >
                        Terms and policies
                      </span>{" "}
                      of{" "}
                      <span className="text-yellowText">Learn Local app</span>.
                    </p>
                    <p className="text-labelTextColor text-base">
                      I acknowledge and agree to abide by all the rules and
                      regulations set forth. By using this app, I commit to
                      adhering to the established guidelines.
                    </p>
                  </div>
                </div>
                {errors.declarationAccepted && (
                  <p className="text-red-500 text-sm">
                    {errors.declarationAccepted.message}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex flex-col gap-1">
                  <label className="custom-label">
                    Is there an ambassador that should be thanked for referring
                    you? (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Ambassador ID / Name"
                    className="custom-input"
                    {...register("ambassadorReferralCode")}
                  />
                  {errors.ambassadorReferralCode && (
                    <p className="text-red-500 text-sm">
                      {errors.ambassadorReferralCode.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 my-4">
                <div className="flex flex-col gap-1">
                  <p className="text-label text-base font-semibold">
                    For security purposes please upload a copy of a valid ID or
                    Driver's License
                  </p>
                </div>
              </div>

              {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 my-4">
                  <div className="flex flex-col gap-1">
                    <label className="custom-label">
                      Security Purposes
                    </label>
                    <input
                      type="text"
                      placeholder="Drivers license or state ID"
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor  outline-none"
                      {...register("driverLicenseOrStateId", {
                        required: "Security Purposes is required",
                      })}
                      value={formData.driverLicenseOrStateId}
                      onChange={handleChange}
                    />
                    {errors.driverLicenseOrStateId && (
                      <p className="text-red-500 text-sm">
                        {errors.driverLicenseOrStateId.message}
                      </p>
                    )}
                  </div>
                </div> */}

              <div className="flex flex-wrap items-center gap-6">
                {/* Front Side Upload */}
                <div>
                  <label className="custom-label">
                    Front Side ID
                  </label>
                  <label className="flex items-center justify-start cursor-pointer mt-3">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={uploadLicenseFrontPic}
                    />
                    <span className="w-[180px] h-[120px] flex items-center justify-center border border-dashed border-gray-300">
                      {formData.driverLicenseFrontPicUrl ? (
                        <img
                          className="w-full h-full object-contain"
                          src={concatPathWithBackendUrl(
                            formData.driverLicenseFrontPicUrl
                          )}
                          alt="Front ID"
                        />
                      ) : (
                        <span className="text-gray-400">
                          Upload Front Image
                        </span>
                      )}
                    </span>
                  </label>
                </div>

                {/* Back Side Upload (Optional) */}
                <div>
                  <label className="text-label text-base font-semibold mt-6">
                    Back Side ID
                  </label>
                  <label className="flex items-center justify-start cursor-pointer mt-3 relative">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={uploadLicenseBackPic}
                    />
                    <span className="w-[180px] h-[120px] flex items-center justify-center border border-dashed border-gray-300 relative">
                      {formData.driverLicenseBackPicUrl ? (
                        <>
                          <img
                            className="w-full h-full object-contain"
                            src={concatPathWithBackendUrl(
                              formData.driverLicenseBackPicUrl
                            )}
                            alt="Back ID"
                          />
                        </>
                      ) : (
                        <span className="text-gray-400">Upload Back Image</span>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="mt-6 flex items-center gap-3">
          {currentStep > 1 && (
            <button
              type="button"
              className="px-4 py-2 bg-primaryBlueBg text-white rounded-full"
              onClick={goPrevious}
            >
              Previous
            </button>
          )}
          {currentStep < 3 ? (
            <button
              type="submit"
              className="px-4 py-2 bg-primaryBlueBg text-white rounded-full"
              onClick={handleSubmit(onSubmit)}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="px-4 py-2 bg-primaryBlueBg text-white rounded-full"
              onClick={handleSubmit(onSubmit)}
            >
              Submit
            </button>
          )}
        </div>
      </form>
      <TermsAndCondition isOpen={termsDrawerOpen} onClose={toggleTermsDrawer} />
    </div>
  );
};

export default HostProfile;
